/*
 * <color-group-switcher>
 *
 * AJAX-ifies the cross-product color carousel on the PDP. Each color/wash is a
 * separate Shopify product (Combined Listings), linked via the custom.color_group
 * metafield and rendered as an <a href> thumbnail carousel. By default clicking a
 * thumbnail is a full-page navigation.
 *
 * Rather than build a new swap engine, this component re-uses the one <product-info>
 * already ships (assets/product-info.js). When an option value points at a *different*
 * product URL, product-info runs a full <main> AJAX swap (viewTransition + <title> +
 * URL update + section cache). That is exactly Dawn's combined-listing behavior, which
 * the theme disabled for color by skipping the color option in the variant picker. We
 * feed carousel clicks straight into it by publishing an `optionValueSelectionChange`
 * whose target is the clicked <a> (carrying data-product-url).
 *
 * Waist/Length carry over: we resolve the customer's stored size to the target
 * color's matching variant id (from that sibling's preloaded page) and swap
 * straight to ?variant=<id>, so the server renders the correct size on first
 * paint — no second round-trip, no Add-to-cart "loading" flash. If the sibling
 * isn't preloaded (or no size stored) we fall back to the default variant + the
 * swapped-in <variant-selects>' own applyStoredSelections() carry-over. See
 * assets/global.js. The carried-size page is also prefetched on idle so the
 * primary swap is a synchronous cache hit.
 *
 * Phase 3 adds: optimistic history (pushState per color so Back steps through colors;
 * popstate hard-reloads), idle preload of sibling colors into the shared
 * __variantSectionCache, and a11y (aria-live announce + focus restore after swap).
 *
 * Rows (client, 2026-09-24): the switcher holds one row per colour family — the
 * product's own custom.color_group, then any family linked through
 * custom.related_color_group (snippets/product-color-group.liquid). Every product
 * renders its OWN family first, so a swap into row 2 would bring back a page with
 * that row on top; freezeColorOrder puts the rows back where the customer first
 * saw them, as it already did for the thumbnails inside a row.
 *
 * Progressive enhancement: the <a href> stays intact, so with no JS (or if the swap
 * engine is absent) the click falls through to normal navigation.
 */
if (!customElements.get('color-group-switcher')) {
  // Bound the shared section cache with an LRU. Three sources write to it — this
  // component's sibling-page preloads, product-variant-section-preload.js, and
  // product-info.js — and without a cap it grows by roughly a whole family every
  // time the customer AJAX-switches to a new color family. Eviction is by least-
  // recently-USED (get() bumps recency), NOT oldest-inserted, so a family the
  // customer keeps returning to (A -> B -> A) survives while stale ones fall out.
  // ~60 entries holds about the current family plus a recent one; tune to taste.
  const CACHE_LIMIT = 60;
  class LruMap extends Map {
    constructor(limit) {
      super();
      this._limit = limit || 60;
    }
    get(key) {
      if (!super.has(key)) return undefined;
      const value = super.get(key);
      super.delete(key);
      super.set(key, value); // move to newest
      return value;
    }
    set(key, value) {
      if (super.has(key)) super.delete(key);
      super.set(key, value);
      while (this.size > this._limit) {
        super.delete(super.keys().next().value); // drop least-recently-used
      }
      return this;
    }
  }
  // Idempotent + order-independent: if another script already made a plain Map,
  // migrate its entries into the LRU and replace the global reference. Consumers
  // read window.__variantSectionCache fresh on each access, so the swap is safe.
  (function ensureLruCache() {
    const existing = window.__variantSectionCache;
    if (existing instanceof LruMap) return;
    const lru = new LruMap(CACHE_LIMIT);
    if (existing && typeof existing.forEach === 'function') {
      existing.forEach((value, key) => lru.set(key, value));
    }
    window.__variantSectionCache = lru;
  })();

  // Single persistent visually-hidden live region, reused across swaps. It must
  // live OUTSIDE the swapped <main>: aria-live only announces mutations to a region
  // that already existed, not the arrival of a brand-new region inside inserted DOM.
  function colorGroupLiveRegion() {
    var region = document.getElementById('ColorGroupLiveRegion');
    if (region) return region;
    region = document.createElement('div');
    region.id = 'ColorGroupLiveRegion';
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('role', 'status');
    region.className = 'visually-hidden';
    document.body.appendChild(region);
    return region;
  }

  customElements.define(
    'color-group-switcher',
    class ColorGroupSwitcher extends HTMLElement {
      connectedCallback() {
        // Freeze the color order to whatever it was when the customer landed on
        // the page. Each product's color_group metafield lists the same siblings
        // in a different order, so a swapped-in carousel arrives in the new
        // product's order; without this it would reshuffle on every switch. The
        // same goes for the rows: every product renders its own family first. On
        // the first connect (full page load) we capture the landing order; on every
        // later connect (after an AJAX swap) we re-apply it. Runs before the
        // carousel scroll init and before paint, so there's no visible reshuffle.
        // A full page reload (incl. popstate, which hard-reloads) recaptures.
        this.freezeColorOrder();

        // Re-init the scroll carousel + active-thumbnail state. On first page
        // load DOMContentLoaded already does this; after an AJAX product swap
        // nothing else does, because HTMLUpdateUtility.viewTransition does not
        // dispatch shopify:section:load. Both helpers are idempotent.
        if (typeof initColorGroupCarousels === 'function') initColorGroupCarousels(this);
        if (typeof syncColorGroupActiveState === 'function') syncColorGroupActiveState(this);

        // Kiwi size chart relocation is normally re-run on shopify:section:load,
        // which viewTransition doesn't fire. Re-anchor the chart next to the
        // carousel after a swap; relocateKsChart retries internally until the
        // Kiwi app has re-injected its chart, and is a no-op if already placed.
        if (typeof relocateKsChart === 'function') relocateKsChart();

        // Ensure the aria-live region exists NOW (idempotent), before any swap.
        // Screen readers only announce mutations to a region that already existed
        // in the DOM — a region created and populated in the same task (which is
        // what happens if we first touch it during a swap) is silently missed on
        // that first announcement.
        colorGroupLiveRegion();

        // If this connect is the result of a color swap (not the initial page
        // load), announce the new color and restore keyboard focus that the DOM
        // replacement dropped. The flag lives on window so it survives the swap
        // that destroyed the instance which set it.
        if (window.__colorGroupPendingSwap) {
          window.__colorGroupPendingSwap = false;
          // iOS Safari repaints the swapped-in main before it has (re)loaded and
          // decoded the carousel's lazy <img>s, so every thumbnail flashes white
          // then pops back in — even though the URLs are identical across
          // siblings and sitting in the HTTP cache. The outgoing main still
          // holds the SAME images fully decoded (viewTransition keeps it in the
          // DOM ~500ms), so move those pixel-ready nodes into the new carousel.
          // connectedCallback runs synchronously during insertion, before first
          // paint, so the adopted images are there from the very first frame.
          this.adoptDecodedThumbnails();
          // The swapped-in carousels arrive at scrollLeft 0; on mobile that hides
          // the color the customer just scrolled to and tapped. Restore the offsets
          // captured at click time. Must run AFTER freezeColorOrder (above) so each
          // offset maps onto the same row and identical item positions, and before
          // refocus below.
          this.restoreScrollPosition();
          this.announceAndRefocus();
          // Warm the now-current product's remaining (below-fold, lazy) gallery
          // images so scrolling down after a swap doesn't popcorn. The above-fold
          // ones were pre-warmed as a sibling before the click.
          if (typeof window.__variantImagePreloadRun === 'function') window.__variantImagePreloadRun();
          // Preserve Kiwi's "Find My Size" widget. Kiwi client-injects it once at
          // page load and does NOT re-hydrate on an AJAX swap, so the swapped-in
          // main has only the un-hydrated app-block mount — the widget would vanish.
          // viewTransition keeps the outgoing main in the DOM ~500ms, so the live
          // widget is still present right now; move it into the new carousel before
          // the old main is removed. A DOM move preserves its click handlers and
          // its body-level modal, and the size chart is identical across color
          // siblings, so reusing the same widget is correct.
          this.preserveSizeChart();
        }

        // Warm sibling colors into the shared section cache on idle so the next
        // color swap resolves from memory. Idempotent (skips cached URLs), so
        // running it on every connect just tops up the newly-demoted product.
        this.schedulePreload();

        if (this._bound) return;
        this._bound = true;
        this.addEventListener('click', this.onClick.bind(this));

        // Re-warm the carried-size variant pages when the customer changes size,
        // so the pre-cache tracks the current selection. Scoped subscription,
        // released in disconnectedCallback so it doesn't leak across swaps.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this._variantChangeUnsub = subscribe(PUB_SUB_EVENTS.variantChange, () => this.schedulePreload());
        }
      }

      disconnectedCallback() {
        if (typeof this._variantChangeUnsub === 'function') {
          this._variantChangeUnsub();
          this._variantChangeUnsub = null;
        }
      }

      onClick(event) {
        const link = event.target.closest('.product__color-group-item');
        if (!link || !this.contains(link)) return;

        // Already the active product — swallow the click, do nothing.
        if (link.classList.contains('product__color-group-item--active')) {
          event.preventDefault();
          return;
        }

        const productUrl = link.dataset.productUrl || link.getAttribute('href');
        if (!productUrl) return;

        const productInfo =
          this.closest('product-info') || document.querySelector("product-info[id^='MainProduct']");

        // No swap engine present -> let the browser navigate normally.
        if (
          !productInfo ||
          productInfo.dataset.updateUrl !== 'true' ||
          typeof publish !== 'function' ||
          typeof PUB_SUB_EVENTS === 'undefined'
        ) {
          return;
        }

        event.preventDefault();
        if (this._loading) return;
        this._loading = true;
        this.setLoading(link, true);

        // History: push a NEW entry duplicating the CURRENT url before handing
        // off. The engine's own updateURL (replaceState) then overwrites this top
        // entry with the target color's url + resolved ?variant= on success — net
        // result [prevColor, newColor], so Back returns to the previous color.
        // We push the *current* url (not the target) on purpose: if the swap
        // fetch fails/aborts, the engine never replaceStates, and we're left with
        // a harmless duplicate entry whose url still matches the displayed
        // product — instead of a URL that says "newColor" over old content.
        // Back/forward is handled by the one-time popstate listener below.
        try {
          window.history.pushState({ colorGroupSwap: true }, '', window.location.href);
        } catch (e) {
          /* pushState can throw on exotic URLs; the swap still proceeds */
        }

        // Capture every row's scroll offset so the swapped-in switcher can
        // restore them — the new <main>'s lists arrive at scrollLeft 0, which on
        // mobile hides the very thumbnail the customer scrolled to and tapped.
        // Stored on window (like the pending-swap flag) because this instance is
        // destroyed with the old main. Keyed by the row's frozen position, which
        // freezeColorOrder matches each incoming row to, and it keeps item
        // positions identical across the swap, so each raw offset maps 1:1.
        const scrollLefts = {};
        this.getRows().forEach((row) => {
          scrollLefts[row.row.dataset.colorGroupRowKey] = row.list.scrollLeft;
        });
        window.__colorGroupScrollLefts = scrollLefts;

        // Tell the next connectedCallback (on the swapped-in switcher) to announce
        // + refocus. Set before publish so it's in place when the swap lands.
        window.__colorGroupPendingSwap = true;
        // Whether a keyboard pressed the thumbnail (Enter, or assistive tech): a
        // mouse or touch click has a detail of 1 or more, and a script's click()
        // is untrusted. Only a keyboard swap takes focus on to the new thumbnail
        // (announceAndRefocus), so pointer users never get a focus ring.
        window.__colorGroupKeyboardSwap = event.isTrusted && event.detail === 0;

        // Resolve the customer's currently-selected size to THIS color's matching
        // variant id (read from the sibling's preloaded page data), so the engine
        // swaps straight to that variant — the server then renders the correct
        // button / price / media for the carried size on first paint. Without
        // this, the swap lands on the sibling's DEFAULT variant and the on-connect
        // carry-over re-selects the size via a second round-trip whose
        // resetProductFormState() briefly DISABLES the Add-to-cart button — that
        // disabled->enabled gap is the "loading" flash. If the sibling isn't
        // preloaded yet, or no size is stored, variantId stays null and we fall
        // back to the default variant + carry-over (unchanged behavior).
        const carriedVariantId = this.resolveCarriedVariantId(productUrl);

        // Hand off to product-info's cross-product swap, after the loading state
        // has painted. The swap parses and inserts a whole <main> (~550ms on a
        // phone), and run inside the click it was all counted in INP.
        const swap = () =>
          publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event: { target: link },
              target: link,
              selectedOptionValues: [],
              selectedOptionMap: {},
              selectedVariantId: carriedVariantId,
              selectedVariant: carriedVariantId ? { id: carriedVariantId } : null,
            },
          });
        if (typeof afterNextPaint === 'function') {
          afterNextPaint(swap);
        } else {
          swap();
        }

        // The old <main> (this element included) is removed ~500ms after the
        // swap, so the loading state disappears with it — no cleanup needed.
        // Guard against a swap that never lands (fetch abort / error): release
        // the lock so the customer can retry.
        window.setTimeout(() => {
          if (!this.isConnected) return;
          this._loading = false;
          this.setLoading(link, false);
        }, 4000);
      }

      adoptDecodedThumbnails() {
        // The outgoing switcher: any that isn't this (new) one and isn't a
        // modal's copy. At this point in the swap the old main is still
        // connected, so its images still hold decoded pixels.
        const oldSwitcher = Array.from(document.querySelectorAll('color-group-switcher')).find(
          (switcher) => switcher !== this && !switcher.closest('quick-add-modal, product-modal')
        );
        if (!oldSwitcher) return;

        this.querySelectorAll('.product__color-group-item').forEach((item) => {
          const id = item.getAttribute('data-color-product-id');
          const newImg = item.querySelector('img');
          if (!id || !newImg) return;

          // Looked up across all of the old switcher's rows, not the same row:
          // a product appears once per switcher, in whichever row holds its family.
          const oldItem = oldSwitcher.querySelector('.product__color-group-item[data-color-product-id="' + id + '"]');
          const oldImg = oldItem && oldItem.querySelector('img');
          if (!oldImg) return;

          // Adopt only a same-asset, actually-painted image: a URL mismatch
          // must never show the wrong color, and swapping in a not-yet-loaded
          // old img would trade one blank for another. (Thumbnails the customer
          // never scrolled to may not have lazy-loaded — those are offscreen in
          // the new carousel too, so their reload is invisible.)
          if (oldImg.getAttribute('src') !== newImg.getAttribute('src')) return;
          if (!oldImg.complete || !oldImg.naturalWidth) return;

          newImg.replaceWith(oldImg);
        });
      }

      restoreScrollPosition() {
        const saved = window.__colorGroupScrollLefts;
        window.__colorGroupScrollLefts = null;
        if (!saved || typeof saved !== 'object') return;

        this.getRows().forEach(({ row, list }) => {
          const left = saved[row.dataset.colorGroupRowKey];
          if (typeof left !== 'number' || left <= 0) return;

          // Items have fixed CSS widths (no image-load dependency), so scrollWidth
          // is already correct at connect time and this assignment lands. The
          // browser clamps to the max scroll, so an out-of-range value is safe.
          list.scrollLeft = left;

          // Belt-and-braces: if layout wasn't final at connect (assignment clamped
          // to 0), re-apply once after the next paint. Skips itself if it stuck or
          // if the customer has already scrolled. rAF (not setTimeout) is right
          // here — this is purely visual and can wait for a visible frame.
          window.requestAnimationFrame(() => {
            if (list.scrollLeft === 0) list.scrollLeft = left;
          });
        });
      }

      announceAndRefocus() {
        // Announce the now-active color. Prefer the heading value (clean color
        // name) of the row holding it, fall back to the active thumbnail's
        // accessible name.
        const active = this.querySelector('.product__color-group-item--active');
        const activeRow = active ? active.closest('[data-color-group-row]') : null;
        const headingValue = (activeRow || this).querySelector('.product__color-group-heading-value');
        let label = headingValue ? headingValue.textContent.trim() : '';
        if (!label && active) {
          const img = active.querySelector('img');
          label = (img && img.getAttribute('alt')) || active.textContent.trim();
        }
        // With two rows the same colour name can sit in both families ("Dark
        // Stone" in each), so the announcement names the row as well.
        const rowName = activeRow ? activeRow.getAttribute('aria-label') : '';
        if (label && rowName && this.getRows().length > 1) {
          label = rowName + ': ' + label;
        }
        if (label) {
          // Clear then set on the next frame so the assignment is always a real
          // mutation. Setting textContent to the value it already holds (e.g. two
          // washes both labelled "Black", or an A->B->A swap) is a no-op that
          // screen readers won't re-announce.
          const region = colorGroupLiveRegion();
          region.textContent = '';
          const message = 'Showing ' + label;
          window.requestAnimationFrame(() => {
            region.textContent = message;
          });
        }

        // The DOM swap removes whatever thumbnail had focus, dropping focus to
        // <body>. Only then do we restore it — to the new active thumbnail —
        // so we never steal focus from a customer interacting elsewhere.
        //
        // "Removes", not "removed": viewTransition (global.js) inserts the new
        // <main> BEFORE it hides and removes the old one, so when this runs a
        // thumbnail pressed from the keyboard still holds focus in the outgoing
        // switcher, and only loses it a moment later. Checking for <body> alone
        // therefore never fired for the keyboard users this exists for; focus on
        // the outgoing switcher after a keyboard press counts as lost too.
        const keyboardSwap = window.__colorGroupKeyboardSwap === true;
        window.__colorGroupKeyboardSwap = false;
        const focused = document.activeElement;
        const focusLost =
          !focused ||
          focused === document.body ||
          (keyboardSwap && !this.contains(focused) && !!focused.closest('color-group-switcher'));
        if (focusLost && active && typeof active.focus === 'function') {
          try {
            active.focus({ preventScroll: true });
          } catch (e) {
            active.focus();
          }
        }
      }

      schedulePreload() {
        // Coalesce rapid triggers (every size change publishes variantChange) into
        // one idle pass.
        if (this._preloadScheduled) return;
        this._preloadScheduled = true;
        const run = () => {
          this._preloadScheduled = false;
          this.preloadSiblings();
        };
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(run, { timeout: 3000 });
        } else {
          window.setTimeout(run, 1200);
        }
      }

      preloadSiblings() {
        const cache = (window.__variantSectionCache = window.__variantSectionCache || new Map());
        const currentPath = window.location.pathname;

        // Round-robin across the rows: the first thumbnail of each row, then the
        // second of each, and so on. The budget below is small on a phone (3), and
        // spent in plain DOM order it would all go on row 1, leaving a related
        // family cold however likely its first colour is to be tapped. With one
        // row this IS plain DOM order, as before.
        const rows = this.getRows().map((row) => row.items);
        const longest = rows.reduce((max, items) => Math.max(max, items.length), 0);
        const links = [];
        for (let position = 0; position < longest; position++) {
          rows.forEach((items) => {
            if (items[position]) links.push(items[position]);
          });
        }

        // A JNCO product page is ~1.04MB. At the old flat MAX of 10 this fired
        // up to 10 sibling pages plus a carried-variant page each — roughly
        // 20MB per colour swap — before the gallery image warms on top. That is
        // a desktop-shaped assumption: on a phone it saturates the connection
        // the customer is actively using, and truncated/failed bodies are
        // exactly what the client's ?pdpdebug=1 capture showed (`gotPI:false`
        // twice on one colour, then `resp sv:NO-NODE`). The two handlers in
        // product-info.js now degrade gracefully when that happens, but not
        // spending the bandwidth is the better half of the fix.
        //
        // Respect the customer's own signal first, then screen size as the
        // proxy for "this is a phone". Preloading is pure optimisation — every
        // path it warms works without it, just slower.
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        if (connection) {
          if (connection.saveData) return;
          if (/(^|-)2g$/.test(connection.effectiveType || '')) return;
        }
        const isSmallScreen = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
        const MAX = isSmallScreen ? 3 : 10;
        let count = 0;

        links.forEach((link) => {
          if (count >= MAX) return;
          if (link.classList.contains('product__color-group-item--active')) return;

          const raw = link.dataset.productUrl;
          if (!raw) return;

          const key = this.siblingKey(raw);
          if (!key || key === currentPath) return;

          count++;

          const cached = cache.get(key);
          if (cached) {
            // Default page already warm — (re)warm the carried-size variant page
            // for the current selection (parse is memoized, so this is cheap) and
            // the gallery images (once per sibling).
            this.warmCarriedPage(key, cache);
            this.warmGalleryImages(key);
            return;
          }

          fetch(key, { credentials: 'same-origin' })
            .then((r) => (r.ok ? r.text() : null))
            .then((text) => {
              // Validate it's a real product render, not a password/throttle/error
              // page — caching one of those would blank the PDP on the next swap.
              if (!text || text.length < 100) return;
              if (text.indexOf('<product-info') === -1) return;
              cache.set(key, text);
              // Warm the carried-size variant page (instant swap) and the gallery
              // images (no first-view popcorn).
              this.warmCarriedPage(key, cache);
              this.warmGalleryImages(key);
            })
            .catch(() => {});
        });
      }

      // The default-page cache key for a sibling: its URL minus the params the
      // engine strips for a cross-product full-page swap. Matches product-info.js
      // buildRequestUrlWithParams so click-time lookups hit byte-for-byte.
      siblingKey(raw) {
        try {
          const u = new URL(raw, window.location.origin);
          u.searchParams.delete('section_id');
          u.searchParams.delete('variant');
          u.searchParams.delete('option_values');
          return u.pathname + u.search;
        } catch (e) {
          return null;
        }
      }

      readStoredOptions() {
        try {
          const raw = window.localStorage.getItem('variant:last-selected-options');
          if (!raw) return null;
          const parsed = JSON.parse(raw);
          return parsed && typeof parsed === 'object' ? parsed : null;
        } catch (e) {
          return null;
        }
      }

      // Parse (once, memoized per instance) a sibling's cached page into the two
      // things we need without a second fetch: its variant list (to map a size
      // selection to that color's variant id) and its main-gallery image
      // descriptors (to warm the HTTP cache and kill first-view popcorn). Returns
      // null (without memoizing) if the page isn't warm yet.
      getSiblingData(key) {
        if (!this._siblingData) this._siblingData = new Map();
        if (this._siblingData.has(key)) return this._siblingData.get(key);

        const cache = window.__variantSectionCache;
        const html = cache && cache.get(key);
        if (!html) return null;

        const data = { variants: null, galleryImages: [] };
        try {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const script = doc.querySelector('variant-selects [data-product-variants]');
          if (script) {
            const arr = JSON.parse(script.textContent || '[]');
            if (Array.isArray(arr)) data.variants = arr;
          }
          data.galleryImages = Array.from(doc.querySelectorAll('.product__media-list img')).map((img) => ({
            src: img.getAttribute('src'),
            srcset: img.getAttribute('srcset'),
            sizes: img.getAttribute('sizes'),
          }));
        } catch (e) {
          /* keep whatever parsed */
        }
        this._siblingData.set(key, data);
        return data;
      }

      // Map the customer's stored sizes to the matching variant id in a sibling.
      // Matches only the option names the sibling actually has (mirroring how
      // applyStoredSelections applies stored options), so a leftover key from a
      // different option system doesn't block a valid jeans match.
      resolveCarriedVariantId(productUrl) {
        const stored = this.readStoredOptions();
        if (!stored) return null;
        const names = Object.keys(stored);
        if (!names.length) return null;

        const key = this.siblingKey(productUrl);
        if (!key) return null;
        const data = this.getSiblingData(key);
        const variants = data && data.variants;
        if (!variants) return null;

        const match = variants.find((v) => {
          const opts = Array.isArray(v.options) ? v.options : [];
          let constrained = 0;
          const ok = names.every((name) => {
            const opt = opts.find((o) => o.name === name);
            if (!opt) return true; // sibling lacks this option — ignore it
            constrained += 1;
            return String(opt.value) === String(stored[name]);
          });
          return ok && constrained > 0;
        });
        return match && match.id != null ? match.id : null;
      }

      // Prefetch the ?variant=<carried> full page so the primary swap is instant.
      // Key matches the engine's cross-product request for a preselected variant.
      warmCarriedPage(key, cache) {
        const carriedId = this.resolveCarriedVariantId(key);
        if (carriedId == null) return;

        let variantKey;
        try {
          const u = new URL(key, window.location.origin);
          u.searchParams.delete('section_id');
          u.searchParams.delete('option_values');
          u.searchParams.set('variant', carriedId);
          variantKey = u.pathname + u.search;
        } catch (e) {
          return;
        }
        if (cache.has(variantKey)) return;

        fetch(variantKey, { credentials: 'same-origin' })
          .then((r) => (r.ok ? r.text() : null))
          .then((text) => {
            if (!text || text.length < 100) return;
            if (text.indexOf('<product-info') === -1) return;
            cache.set(variantKey, text);
          })
          .catch(() => {});
      }

      // Warm the sibling's above-the-fold gallery images into the browser's HTTP
      // cache (image responses ARE cacheable, unlike section renders), so the first
      // switch to that color paints them together instead of popcorning them in as
      // they download (~500ms spread, measured). Mirrors product-variant-image-
      // preload.js. Desktop uses a "columns" layout showing several images at once,
      // so warm ~8; mobile is a one-at-a-time slider, so 2 is enough. Once per
      // sibling per instance.
      warmGalleryImages(key) {
        if (!this._galleryWarmed) this._galleryWarmed = new Set();
        if (this._galleryWarmed.has(key)) return;

        const data = this.getSiblingData(key);
        if (!data || !data.galleryImages.length) return;
        this._galleryWarmed.add(key);

        const isMobile = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
        const limit = isMobile ? 2 : 8;
        data.galleryImages.slice(0, limit).forEach((d) => {
          if (!d.src && !d.srcset) return;
          const pre = new Image();
          pre.decoding = 'async';
          // Set srcset + sizes before src so the browser picks the SAME candidate
          // URL the gallery <img> will request when shown.
          if (d.srcset) pre.srcset = d.srcset;
          if (d.sizes) pre.sizes = d.sizes;
          if (d.src) pre.src = d.src;
        });
      }

      // The switcher's rows in DOM order, one per colour family, each with its
      // list, its thumbnails and their product ids.
      getRows() {
        return Array.from(this.querySelectorAll('[data-color-group-row]')).reduce((rows, row) => {
          const list = row.querySelector('[data-color-product-list]');
          if (!list) return rows;
          const items = Array.from(list.querySelectorAll('.product__color-group-item'));
          rows.push({ row, list, items, ids: items.map((item) => item.dataset.colorProductId) });
          return rows;
        }, []);
      }

      freezeColorOrder() {
        const rows = this.getRows();
        if (!rows.length) return;

        // First switcher of this page load defines the canonical order: which row
        // sits where, and the thumbnail order inside each (captured once on window
        // so it survives the swaps that destroy each instance).
        const frozen = window.__colorGroupFrozenRows;
        if (!Array.isArray(frozen) || !frozen.length) {
          window.__colorGroupFrozenRows = rows.map((row) => row.ids.slice());
          rows.forEach((row, index) => {
            row.row.dataset.colorGroupRowKey = String(index);
          });
          return; // already in landing order — nothing to reorder
        }

        // Match each incoming row to the frozen row it shares a product with.
        // Families don't overlap, so one shared id names the family; the row's
        // position can't, because every product renders its own family first. A
        // family not seen yet this visit joins the frozen order after the known
        // ones, so it too keeps its place on later swaps.
        const claimed = new Set();
        rows.forEach((row) => {
          let key = frozen.findIndex(
            (ids, index) => !claimed.has(index) && row.ids.some((id) => ids.includes(id))
          );
          if (key === -1) {
            frozen.push(row.ids.slice());
            key = frozen.length - 1;
          }
          claimed.add(key);
          row.key = key;
          row.row.dataset.colorGroupRowKey = String(key);
        });

        // Re-apply the frozen order. insertBefore/appendChild MOVE existing nodes
        // (no clone), so handlers/state are preserved. Rows first, then the
        // thumbnails inside each: ones present in the frozen order go first, in
        // that order; any not in it keep their order after, and join it.
        const ordered = rows.slice().sort((a, b) => a.key - b.key);
        if (ordered.some((row, index) => row !== rows[index])) {
          const parent = rows[0].row.parentNode;
          const after = rows[rows.length - 1].row.nextSibling;
          ordered.forEach((row) => parent.insertBefore(row.row, after));
        }

        ordered.forEach((row) => {
          const order = frozen[row.key];
          const byId = new Map(row.items.map((item) => [item.dataset.colorProductId, item]));
          order.forEach((id) => {
            const el = byId.get(id);
            if (el) {
              row.list.appendChild(el);
              byId.delete(id);
            }
          });
          byId.forEach((el, id) => {
            row.list.appendChild(el);
            order.push(id);
          });
        });
      }

      preserveSizeChart() {
        // The outgoing main (hidden, not yet removed) still holds the hydrated
        // widget; the incoming main has none. Move the survivor into this new
        // carousel, in the same spot relocateKsChart uses.
        const chart = document.querySelector('.ks-chart-container');
        if (!chart || this.contains(chart)) return;
        const anchor = this.querySelector('[data-color-product-container]');
        if (anchor) anchor.insertAdjacentElement('afterend', chart);
      }

      setLoading(link, isLoading) {
        link.classList.toggle('product__color-group-item--loading', isLoading);
      }
    }
  );

  // One-time popstate handler for back/forward across color entries. Reloading is
  // the pragmatic, correct choice (matches JiYu): it lets the server render the
  // target color authoritatively rather than reverse-engineering a swap. We only
  // reload when the history target is a *different product path* than what's shown
  // — a same-path variant change (?variant=) must not trigger a reload.
  if (!window.__colorGroupPopstateBound) {
    window.__colorGroupPopstateBound = true;
    window.addEventListener('popstate', () => {
      const shown = document.querySelector("product-info[id^='MainProduct']");
      const shownUrl = shown && shown.dataset.url;
      if (!shownUrl) return;
      let shownPath;
      try {
        shownPath = new URL(shownUrl, window.location.origin).pathname;
      } catch (e) {
        return;
      }
      if (window.location.pathname !== shownPath) {
        window.location.reload();
      }
    });
  }
}
